import { createFileRoute, Link, Navigate, redirect, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { displayName } from '@promanage/contracts/person-display';
import type { PersonInput } from '@promanage/contracts';
import { formatRef } from '@promanage/core';
import { Button, Ref } from '@promanage/ui';
import { TopBar } from '../components/TopBar';
import { useUnsavedChanges } from '../components/UnsavedChanges';
import { resolvePersonRef } from '../features/people/personRef';
import { PersonForm, toFormValues } from '../features/people/PersonForm';
import { trpc } from '../lib/trpc';

/**
 * Edit a person's own details — name, contact, notes.
 *
 * Roles and relationships are NOT edited here. They carry start dates and become history the
 * moment they end, so they are changed from the record they attach to, with their own dates.
 *
 * Unlike the record page this route renders no "not found" screens of its own: anything that
 * does not resolve to a person is handed to `/people/$ref`, which already explains all three
 * ways a reference can fail. One place to get that wording right.
 */
export const Route = createFileRoute('/people/$ref/edit')({
  beforeLoad: ({ params }) => {
    const resolved = resolvePersonRef(params.ref);

    // Normalise first — `pe3` edits the same record as PE000003.
    if (resolved.status === 'ok' && resolved.canonical !== params.ref) {
      throw redirect({
        to: '/people/$ref/edit',
        params: { ref: resolved.canonical },
        replace: true,
      });
    }

    // Only the SHAPE is checked here now. Whether the record exists is the server's answer,
    // and `beforeLoad` cannot wait on it without blocking the route transition on a fetch —
    // so a ref that resolves to nothing is handled below, by sending it to the record page.
    if (resolved.status !== 'ok') {
      // Terminates: the record page only redirects a ref that resolves AND is not canonical.
      throw redirect({ to: '/people/$ref', params: { ref: params.ref }, replace: true });
    }
  },
  component: EditPerson,
});

function EditPerson() {
  const { ref } = Route.useParams();
  const navigate = useNavigate();
  const unsaved = useUnsavedChanges();
  const queryClient = useQueryClient();

  const resolved = resolvePersonRef(ref);
  const refSeq = resolved.status === 'ok' ? resolved.sequence : 1;

  const person = useQuery(
    trpc.people.get.queryOptions({ refSeq }, { staleTime: 10_000, retry: false }),
  );

  const update = useMutation(
    trpc.people.update.mutationOptions({
      meta: { errorTitle: 'Could not save those changes' },
      onSuccess() {
        unsaved.saved();
        void queryClient.invalidateQueries({ queryKey: trpc.people.get.queryKey() });
        void queryClient.invalidateQueries({ queryKey: trpc.people.list.queryKey() });
        void navigate({ to: '/people/$ref', params: { ref: formatRef('person', refSeq) } });
      },
    }),
  );

  if (person.isPending) {
    return (
      <div className="mx-auto max-w-[var(--content-max)] pb-4">
        <TopBar title="Edit person" subtitle={formatRef('person', refSeq)} />
      </div>
    );
  }

  // A ref nobody has issued, or another agency's. The record page owns that wording, and its
  // three separate messages are better than a fourth written here.
  if (person.error || !person.data) {
    return <Navigate to="/people/$ref" params={{ ref: formatRef('person', refSeq) }} replace />;
  }

  const record = person.data;

  const save = (values: PersonInput) => update.mutate({ personId: record.id, values });

  return (
    <div className="mx-auto max-w-[var(--content-max)] pb-4">
      <TopBar
        title="Edit person"
        subtitle={`${formatRef('person', record.refSeq)} · ${displayName(record)}`}
      />

      <div className="contents" onChange={unsaved.touch}>
        <PersonForm
          initial={toFormValues(record)}
          excludePersonId={record.id}
          submitLabel="Save changes"
          onSubmit={save}
          pending={update.isPending}
          cancel={
            <Link to="/people/$ref" params={{ ref: formatRef('person', record.refSeq) }}>
              <Button type="button" variant="secondary">
                Cancel
              </Button>
            </Link>
          }
          footnote={
            <>
              Editing <Ref entity="person" sequence={record.refSeq} /> — roles and relationships are
              changed from the records they attach to.
            </>
          }
        />
      </div>
    </div>
  );
}
